import React, { useCallback, useMemo } from 'react';

import {
  OvalTablePathEnumsLeft,
  OvalTablePathEnumsRight,
  OvalTableRectEnums_35_26,
  OvalTableRectEnumsBottom,
  OvalTableRectEnumsTop,
} from '../../enums/ovalTableEnums';
import OvalCenterCombinations from './OvalCenterCombinations';
import { doChipStore } from '../../store/doChipStore';
import { useRouletteDoBetValidation } from '../../hooks/useRouletteDoBetValidation';
import { BetType, CombinationTypes, OvalTableByIndex, REC_BET_DATA_MODEL_MAP } from '../../enums/tableNumbers';
import { observer } from 'mobx-react-lite';
import { uuid } from '../../helpers/uuid';
import ChipOnTable from '../chip/chip-on-table';
import { marketType } from '../../enums/marketType';
import { getKeyByValue } from '../../helpers/getKeyByValue';
import { parseNumberByPrecision } from '@pascal/pascal-core';
import { winningAmountStore } from '../../store/winningAmountStore';
import { gameStore } from '../../store/gameStore';
import { mathConfig, mathJS } from '../../helpers/mathFormat';
import { LOG2E } from 'mathjs';

const OvalTable = observer(
  ({
    isReplay,
    isSecondaryTable,
    selectedData,
    handleMouseMove = () => {},
    handleMouseLeave = () => {},
    isShowNeighbors = true,
  }) => {
    const { selectedChip, setNeighbors, neighbors, getAllBetsModels: allBetsModels } = doChipStore;
    const { handleDoBetInCombination, handleDoBetWithValid } = useRouletteDoBetValidation();
    const { winNumber } = winningAmountStore;
    const { isBetsClosed } = gameStore;

    const memoAllChipsTypes = useMemo(() => {
      const lastBetsModels = allBetsModels.length ? allBetsModels[0] : [];

      if (lastBetsModels.betModel) {
        return lastBetsModels?.betModel?.reduce((acc, curr) => {
          const { bet, includedBets } = curr;
          const betTypeName = getKeyByValue(marketType, bet.type);
          if (betTypeName.includes(BetType.Neighbors) || betTypeName.includes(BetType.Finale)) {
            includedBets && acc.push(...includedBets.map((item) => getKeyByValue(marketType, item.type)));
          } else if (!includedBets) {
            acc.push(getKeyByValue(marketType, bet.type));
          }
          return acc;
        }, []);
      }
      return [];
    }, [allBetsModels]);

    const handleDoChip = (e) => {
      const valueType = e.target.getAttribute('data');
      if (neighbors) {
        const OvalTableToNeighbors = [...OvalTableByIndex, ...OvalTableByIndex, ...OvalTableByIndex];
        const valueTypeIndex = OvalTableToNeighbors.findIndex(
          (item, index) => item === valueType && index > OvalTableByIndex.length
        );
        const neighborsData = OvalTableToNeighbors.slice(valueTypeIndex - neighbors, valueTypeIndex + neighbors + 1);
        if (neighborsData) {
          if (selectedChip) {
            handleDoBetInCombination(neighborsData, selectedChip, 'Neighbors_' + valueType);
          }
        }
      } else {
        const model = REC_BET_DATA_MODEL_MAP[valueType];
        if (model) {
          const nextChipModel = {
            [model.type]: selectedChip,
            chipType: model.type,
          };
          if (selectedChip) {
            handleDoBetWithValid(nextChipModel);
          }
        }
      }
    };

    const handleDoChipInCombination = (e) => {
      const valueType = e.target.getAttribute('data');
      const model = CombinationTypes[valueType];
      if (model) {
        if (selectedChip) {
          handleDoBetInCombination(model, selectedChip, valueType);
        }
      }
    };

    const isActiveOvalBlock = (item) => {
      if (selectedData?.includes(item.value)) {
        if (selectedData?.length === 1) {
          return true;
        }
        return !!(selectedData?.includes('neighbors') || selectedData?.includes('OvalCombinationBets'));
      }
    };

    const handleGetColumnChip = useCallback(
      (typeName) => {
        const lastBetsModels = allBetsModels.length ? allBetsModels[0] : [];
        if (lastBetsModels.betModel) {
          const betsForOval = lastBetsModels?.betModel?.reduce((acc, curr) => {
            const { bet, includedBets } = curr;
            const betTypeName = getKeyByValue(marketType, bet.type);
            if (betTypeName.includes(BetType.Neighbors) || betTypeName.includes(BetType.Finale)) {
              acc.push(...includedBets);
            } else if (!includedBets) {
              acc.push(bet);
            }
            return acc;
          }, []);
          return betsForOval.reduce((acc, cur) => {
            if (getKeyByValue(marketType, cur.type) === typeName) {
              if (acc[typeName]) {
                acc[typeName] = parseNumberByPrecision(
                  1,
                  +mathJS.format(
                    +(
                      acc[typeName] +
                      (cur.hasOwnProperty('winAmount') ? (cur.winAmount ? cur.winAmount : 0) : cur.amount)
                    ),
                    mathConfig
                  )
                );
              } else {
                acc[typeName] = cur.hasOwnProperty('winAmount') ? (cur.winAmount ? cur.winAmount : 0) : cur.amount;
              }
            }
            return acc;
          }, {});
        }
      },
      [allBetsModels]
    );

    const handleRightEnums = () =>
      OvalTablePathEnumsRight.map((item) => {
        const columnChip = handleGetColumnChip(item.data);
        return (
          <div
            className={`oval-block ${winNumber === item.value ? 'win' : ''} ${
              !isBetsClosed && isActiveOvalBlock(item) ? 'active' : ''
            }`}
            key={uuid()}
            onClick={handleDoChip}
          >
            <svg width={item.width} height={item.height} viewBox={item.viewBox} xmlns='http://www.w3.org/2000/svg'>
              <g id={item.gId} data-name={item.gDataName} transform={item.gTransform}>
                <path
                  data={item.data}
                  id={item.pathId}
                  data-name={item.pathDataName}
                  d={item.d}
                  transform={item.pathTransform}
                  stroke='#b2b2b2'
                  strokeMiterlimit='10'
                  fill={item.fill}
                />
              </g>
            </svg>
            {!columnChip?.betType ||
            (columnChip?.betType &&
              (columnChip?.betType.includes(BetType.Neighbors) || columnChip?.betType.includes(BetType.Finale))) ? (
              <ChipOnTable
                type={item.data}
                columnChips={columnChip}
                child={() => <p>{item.value}</p>}
                allChipsTypes={memoAllChipsTypes}
              />
            ) : (
              <p>{item.value}</p>
            )}
          </div>
        );
      });

    const handleBottomEnums = () =>
      OvalTableRectEnumsBottom.map((item) => {
        const columnChip = handleGetColumnChip(item.data);
        return (
          <div
            className={`oval-block ${winNumber === item.value ? 'win' : ''} ${
              !isBetsClosed && isActiveOvalBlock(item) ? 'active' : ''
            }`}
            key={uuid()}
            onClick={handleDoChip}
          >
            <svg width='24.904' height='30.948' viewBox='0 0 24.904 30.948' xmlns='http://www.w3.org/2000/svg'>
              <g id={item.gId} data-name={item.gDataName} transform={item.gTransform}>
                <rect
                  data={item.data}
                  width='29.948'
                  height='23.904'
                  strokeWidth='1'
                  stroke='#b2b2b2'
                  fill={item.fill}
                  id={item.rectId}
                  strokeMiterlimit='10'
                  data-name={item.rectDataName}
                  transform={item.rectTransform}
                />
              </g>
            </svg>
            {!columnChip?.betType ||
            (columnChip?.betType &&
              (columnChip?.betType.includes(BetType.Neighbors) || columnChip?.betType.includes(BetType.Finale))) ? (
              <ChipOnTable
                type={item.data}
                columnChips={columnChip}
                child={() => <p>{item.value}</p>}
                allChipsTypes={memoAllChipsTypes}
              />
            ) : (
              <p>{item.value}</p>
            )}
          </div>
        );
      });

    const handleLeftEnums = () =>
      OvalTablePathEnumsLeft.map((item) => {
        const columnChip = handleGetColumnChip(item.data);
        return (
          <div
            className={`oval-block ${winNumber === item.value ? 'win' : ''} ${
              !isBetsClosed && isActiveOvalBlock(item) ? 'active' : ''
            }`}
            key={uuid()}
            onClick={handleDoChip}
          >
            <svg width={item.width} height={item.height} viewBox={item.viewBox} xmlns='http://www.w3.org/2000/svg'>
              <g id={item.gId} data-name={item.gDataName} transform={item.gTransform}>
                <path
                  data={item.data}
                  id={item.pathId}
                  data-name={item.pathDataName}
                  d={item.d}
                  transform={item.pathTransform}
                  stroke='#b2b2b2'
                  strokeMiterlimit='10'
                  strokeWidth='1'
                  fill={item.fill}
                />
              </g>
            </svg>
            {!columnChip?.betType ||
            (columnChip?.betType &&
              (columnChip?.betType.includes(BetType.Neighbors) || columnChip?.betType.includes(BetType.Finale))) ? (
              <ChipOnTable
                type={item.data}
                columnChips={columnChip}
                child={() => <p>{item.value}</p>}
                allChipsTypes={memoAllChipsTypes}
              />
            ) : (
              <p>{item.value}</p>
            )}
          </div>
        );
      });

    const handleTopEnums = () =>
      OvalTableRectEnumsTop.map((item) => {
        let columnChip = handleGetColumnChip(item.data);

        return (
          <div
            className={`oval-block ${winNumber === item.value ? 'win' : ''} ${
              !isBetsClosed && isActiveOvalBlock(item) ? 'active' : ''
            }`}
            key={uuid()}
            onClick={handleDoChip}
          >
            <svg width='24.904' height='30.948' viewBox='0 0 24.904 30.948' xmlns='http://www.w3.org/2000/svg'>
              <g id={item.gId} data-name={item.gDataName} transform={item.gTransform}>
                <rect
                  data={item.data}
                  width='29.948'
                  height='23.904'
                  strokeWidth='1'
                  stroke='#b2b2b2'
                  fill={item.fill}
                  id={item.rectId}
                  strokeMiterlimit='10'
                  data-name={item.rectDataName}
                  transform={item.rectTransform}
                />
              </g>
            </svg>
            {!columnChip?.betType ||
            (columnChip?.betType &&
              (columnChip?.betType.includes(BetType.Neighbors) || columnChip?.betType.includes(BetType.Finale))) ? (
              <ChipOnTable
                type={item.data}
                columnChips={columnChip}
                child={() => <p>{item.value}</p>}
                allChipsTypes={memoAllChipsTypes}
              />
            ) : (
              <p>{item.value}</p>
            )}
          </div>
        );
      });

    const handleRightsEnums = () =>
      OvalTableRectEnums_35_26.map((item) => {
        const columnChip = handleGetColumnChip(item.data);
        return (
          <div
            className={`oval-block ${winNumber === item.value ? 'win' : ''} ${
              !isBetsClosed && isActiveOvalBlock(item) ? 'active' : ''
            }`}
            key={uuid()}
            onClick={handleDoChip}
          >
            <svg width={item.width} height={item.height} viewBox={item.viewBox} xmlns='http://www.w3.org/2000/svg'>
              <g id={item.gId} data-name={item.gDataName} transform={item.gTransform}>
                <path
                  data={item.data}
                  id={item.pathId}
                  data-name={item.pathDataName}
                  d={item.d}
                  transform={item.pathTransform}
                  stroke='#b2b2b2'
                  strokeMiterlimit='10'
                  strokeWidth='1'
                  fill={item.fill}
                />
              </g>
            </svg>
            {!columnChip?.betType ||
            (columnChip?.betType &&
              (columnChip?.betType.includes(BetType.Neighbors) || columnChip?.betType.includes(BetType.Finale))) ? (
              <ChipOnTable
                type={item.data}
                columnChips={columnChip}
                child={() => <p>{item.value}</p>}
                allChipsTypes={memoAllChipsTypes}
              />
            ) : (
              <p>{item.value}</p>
            )}
          </div>
        );
      });

    return (
      <div
        className='right-cl-mid-wrp'
        onMouseMove={(e) => handleMouseMove(e, neighbors)}
        onMouseLeave={handleMouseLeave}
      >
        <div className='mid-main-wrp'>
          <div className={`mid-main-game-cont ${isSecondaryTable ? 'small' : ''}`}>
            <OvalCenterCombinations
              handleDoChip={handleDoChipInCombination}
              handleGetColumnChip={handleGetColumnChip}
              memoAllChipsTypes={memoAllChipsTypes}
            />
            <svg>
              <defs>
                <linearGradient id='redGrad' y1='0.677' x2='1.057' y2='0.665' gradientUnits='objectBoundingBox'>
                  <stop offset='0' stopColor='#b00000' />
                  <stop offset='1' stopColor='#580000' />
                </linearGradient>
                <linearGradient id='greenGrad' y1='0.677' x2='1.057' y2='0.665' gradientUnits='objectBoundingBox'>
                  <stop offset='0' stopColor='#007f53' />
                  <stop offset='1' stopColor='#003d00' />
                </linearGradient>
                <linearGradient id='blackGrad' y1='0.677' x2='1.057' y2='0.665' gradientUnits='objectBoundingBox'>
                  <stop offset='0' stopColor='#232323' />
                  <stop offset='1' />
                </linearGradient>
              </defs>
            </svg>
            <div className='mid-main-oval-roulette'>
              {handleRightEnums()}
              {handleBottomEnums()}
              {handleLeftEnums()}
              {handleTopEnums()}
              {handleRightsEnums()}
            </div>
          </div>
          {isShowNeighbors ? (
            <div className={'mid-main-game-right'}>
              <button
                className={`plus-minus-btn plus ${isReplay ? 'disabled' : ''}`}
                onClick={() => setNeighbors('inc')}
              />
              <div className='count-box'>
                <p className='count-number'>{neighbors}</p>
              </div>
              <button
                className={`plus-minus-btn minus ${isReplay ? 'disabled' : ''}`}
                onClick={() => setNeighbors('dec')}
              />
            </div>
          ) : null}
        </div>
      </div>
    );
  }
);

export default OvalTable;
